import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { type HelpArticleKind } from "@fitness-app/data-models/entities/HelpArticle";
import {
  type HelpArticleReader,
  type HelpArticleStats,
  type HelpStatsPeriod,
} from "@fitness-app/data-models/entities/HelpArticleStats";

import { analyticsApi } from "~/store/initializeStore";

// Serwis trzyma wyniki 10 minut — częstsze pytanie i tak dostałoby to samo.
const STALE_TIME_MS = 5 * 60 * 1000;

export const helpStatsKey = (kind: string, slug: string) => `${kind}:${slug}`;

/**
 * 503 znaczy, że środowisko nie ma klucza do odczytu PostHoga (np. staging) —
 * to brak statystyk, a nie awaria, więc bez ponawiania i bez czerwonego błędu.
 */
export const isHelpStatsUnavailable = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 503;

const retryUnlessUnavailable = (failureCount: number, error: unknown) =>
  !isHelpStatsUnavailable(error) && failureCount < 2;

/** Statystyki czytania wszystkich materiałów (TRE-2251), po kluczu `kind:slug`. */
export const useHelpArticleStats = (days: HelpStatsPeriod) =>
  useQuery({
    queryKey: ["help_article_stats", days],
    queryFn: async () => {
      const { data } = await analyticsApi.get<HelpArticleStats[]>("/help-center/stats", { params: { days } });
      return new Map(data.map((row) => [helpStatsKey(row.kind, row.slug), row]));
    },
    staleTime: STALE_TIME_MS,
    retry: retryUnlessUnavailable,
  });

export const useHelpArticleReaders = (
  article: { kind: HelpArticleKind; slug: string } | null,
  days: HelpStatsPeriod,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["help_article_readers", article?.kind, article?.slug, days],
    queryFn: async () => {
      const { data } = await analyticsApi.get<HelpArticleReader[]>("/help-center/stats/readers", {
        params: { kind: article?.kind, slug: article?.slug, days },
      });
      return data;
    },
    enabled: enabled && !!article,
    staleTime: STALE_TIME_MS,
    retry: retryUnlessUnavailable,
  });
